import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Fade,
  Zoom,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

interface SelfieUploadProps {
  existingPhotoUrl: string | boolean | null;
  onPhotoUploaded: (hasPhoto: boolean | null) => void;
  required?: boolean;
  error?: string | null;
}

function dataURLToBlob(dataURL: string): Blob | null {
  try {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  } catch {
    return null;
  }
}

const SelfieUpload: React.FC<SelfieUploadProps> = ({
  existingPhotoUrl,
  onPhotoUploaded,
  required = true,
  error: externalError,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { accessToken, refreshAccessToken } = useAuth();
  const primaryColor = theme.palette.primary.main;

  const [hasPhoto, setHasPhoto] = useState(!!existingPhotoUrl);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [flashEffect, setFlashEffect] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auth-aware fetch that retries on 401
  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      } as Record<string, string>;
      const response = await fetch(url, { ...options, headers });
      if (response.status === 401) {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          const retryHeaders = {
            ...options.headers,
            Authorization: `Bearer ${refreshedToken}`,
          } as Record<string, string>;
          return fetch(url, { ...options, headers: retryHeaders });
        }
      }
      return response;
    },
    [accessToken, refreshAccessToken]
  );

  const setPhotoPreviewSafe = useCallback((newUrl: string | null) => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch {
        // ignore
      }
    }
    blobUrlRef.current = newUrl;
    setPhotoPreview(newUrl);
  }, []);

  // Fetch existing photo on mount
  useEffect(() => {
    const fetchExistingPhoto = async () => {
      if (!existingPhotoUrl || !accessToken) return;
      setIsLoadingPhoto(true);
      try {
        const response = await authFetch(`/api/caregiver/selfie?t=${Date.now()}`);
        if (!response.ok) {
          if (response.status !== 404) console.error('[SelfieUpload] Error fetching existing photo, status:', response.status);
          setHasPhoto(false);
          return;
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPhotoPreviewSafe(blobUrl);
        setHasPhoto(true);
      } catch (err) {
        console.error('[SelfieUpload] Error fetching existing photo:', err);
        setHasPhoto(false);
      } finally {
        setIsLoadingPhoto(false);
      }
    };
    fetchExistingPhoto();
  }, [existingPhotoUrl, accessToken, authFetch, setPhotoPreviewSafe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((track) => track.stop());
      if (blobUrlRef.current) {
        try {
          URL.revokeObjectURL(blobUrlRef.current);
        } catch {
          // ignore
        }
        blobUrlRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [stream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCameraReady(false);
    setCountdown(null);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, [stream]);

  const startCamera = async () => {
    try {
      setError(null);
      setShowCamera(true);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 720 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 1 },
        },
      });
      setStream(mediaStream);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.onloadedmetadata = () => setCameraReady(true);
        }
      }, 100);
    } catch (err: any) {
      setShowCamera(false);
      if (err.name === 'NotAllowedError') {
        setError(t('selfie.cameraAccessDenied'));
      } else {
        setError(t('selfie.cameraError'));
      }
    }
  };

  const uploadPhoto = async (fileOrBlob: Blob) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const localPreviewUrl = URL.createObjectURL(fileOrBlob);
    setPhotoPreviewSafe(localPreviewUrl);

    try {
      const formData = new FormData();
      formData.append('selfie', fileOrBlob, (fileOrBlob as File).name || 'selfie.jpg');

      const xhr = new XMLHttpRequest();
      const uploadPromise = new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(xhr.responseText || 'Upload failed'));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', '/api/caregiver/selfie');
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.send(formData);
      });

      await uploadPromise;

      // Fetch server-processed version
      try {
        const photoResponse = await authFetch(`/api/caregiver/selfie?t=${Date.now()}`);
        if (photoResponse.ok) {
          const serverBlob = await photoResponse.blob();
          const serverBlobUrl = URL.createObjectURL(serverBlob);
          setPhotoPreviewSafe(serverBlobUrl);
        }
      } catch {
        // Keep local preview
      }

      setHasPhoto(true);
      onPhotoUploaded(true);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(t('selfie.uploadFailed'));
      setPhotoPreviewSafe(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 200);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    const size = Math.min(video.videoWidth, video.videoHeight);
    const x = (video.videoWidth - size) / 2;
    const y = (video.videoHeight - size) / 2;

    canvas.width = 600;
    canvas.height = 600;

    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, x, y, size, size, 0, 0, 600, 600);

    canvas.toBlob(
      async (blob) => {
        if (blob) {
          await uploadPhoto(blob);
        } else {
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.9);
            const fallbackBlob = dataURLToBlob(dataURL);
            if (fallbackBlob) {
              await uploadPhoto(fallbackBlob);
            } else {
              setError(t('selfie.cameraError'));
            }
          } catch {
            setError(t('selfie.cameraError'));
          }
        }
      },
      'image/jpeg',
      0.9
    );

    stopCamera();
  };

  const initiateCapture = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setCountdown(3);
    const intervalId = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) {
          clearInterval(intervalId);
          countdownIntervalRef.current = null;
          return null;
        }
        if (prev <= 1) {
          clearInterval(intervalId);
          countdownIntervalRef.current = null;
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    countdownIntervalRef.current = intervalId;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError(t('selfie.invalidFileType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('selfie.fileTooLarge'));
      return;
    }

    await uploadPhoto(file);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await authFetch('/api/caregiver/selfie', { method: 'DELETE' });
      setHasPhoto(false);
      setPhotoPreviewSafe(null);
      onPhotoUploaded(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(t('selfie.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const displayError = error || externalError;

  // Camera View
  if (showCamera) {
    return (
      <Card variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {t('selfie.takeYourPhoto')}
            </Typography>
            <IconButton onClick={stopCamera} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              margin: '0 auto',
              aspectRatio: '1',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
              }}
            />

            {/* Viewfinder overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
              }}
            >
              {/* Corner brackets */}
              {[
                { top: 20, left: 20, borderTop: `3px solid ${primaryColor}`, borderLeft: `3px solid ${primaryColor}`, borderRadius: '8px 0 0 0' },
                { top: 20, right: 20, borderTop: `3px solid ${primaryColor}`, borderRight: `3px solid ${primaryColor}`, borderRadius: '0 8px 0 0' },
                { bottom: 20, left: 20, borderBottom: `3px solid ${primaryColor}`, borderLeft: `3px solid ${primaryColor}`, borderRadius: '0 0 0 8px' },
                { bottom: 20, right: 20, borderBottom: `3px solid ${primaryColor}`, borderRight: `3px solid ${primaryColor}`, borderRadius: '0 0 8px 0' },
              ].map((style, i) => (
                <Box key={i} sx={{ position: 'absolute', width: 40, height: 40, ...style }} />
              ))}

              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70%',
                  height: '70%',
                  border: '2px dashed rgba(255,255,255,0.3)',
                  borderRadius: '50%',
                }}
              />
            </Box>

            {/* Countdown overlay */}
            {countdown && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.5)',
                }}
              >
                <Zoom in key={countdown}>
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'white',
                      fontSize: '120px',
                      fontWeight: 'bold',
                      textShadow: `0 0 40px ${primaryColor}80`,
                    }}
                  >
                    {countdown}
                  </Typography>
                </Zoom>
              </Box>
            )}

            {/* Flash effect */}
            <Fade in={flashEffect}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'white' }} />
            </Fade>

            {/* Loading spinner */}
            {!cameraReady && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.7)',
                }}
              >
                <CircularProgress sx={{ color: primaryColor }} />
              </Box>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>

          {/* Capture button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, p: 3 }}>
            <Button
              variant="contained"
              onClick={initiateCapture}
              disabled={!cameraReady || countdown !== null}
              sx={{
                width: 70,
                height: 70,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                border: '4px solid white',
                boxShadow: `0 0 20px ${primaryColor}80`,
                minWidth: 'unset',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${primaryColor} 100%)`,
                  transform: 'scale(1.05)',
                },
                '&:disabled': { background: '#666', border: '4px solid #999' },
                transition: 'all 0.2s ease',
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: 32, color: 'white' }} />
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', pb: 2, px: 2 }}
          >
            {t('selfie.positionFaceTip')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Main view
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <CameraIcon sx={{ mb: 1, mr: 1, color: primaryColor }} />
          <Typography variant="h6" gutterBottom>
            {t('selfie.yourProfilePhoto')}
            {required && (
              <span style={{ color: '#f44336', marginLeft: 4 }}>*</span>
            )}
          </Typography>
        </Box>

        {isLoadingPhoto ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Box
              sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CircularProgress sx={{ color: primaryColor }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('selfie.loadingPhoto')}
            </Typography>
          </Box>
        ) : photoPreview ? (
          <Fade in>
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    border: hasPhoto ? '4px solid #4caf50' : `4px solid ${primaryColor}`,
                  }}
                >
                  <img
                    src={photoPreview}
                    alt={t('selfie.selfiePreviewAlt')}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />

                  {/* Upload progress overlay */}
                  {isUploading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.7)',
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        value={uploadProgress}
                        size={60}
                        sx={{ color: primaryColor, mb: 1 }}
                      />
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                        {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {/* Delete progress overlay */}
                  {isDeleting && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.7)',
                      }}
                    >
                      <CircularProgress size={60} sx={{ color: '#f44336', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                        {t('selfie.deleting')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Success badge */}
                {hasPhoto && !isUploading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                      zIndex: 1,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              {!isUploading && !isDeleting && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={removePhoto}
                    startIcon={<DeleteIcon />}
                    sx={{
                      borderColor: '#f44336',
                      color: '#f44336',
                      '&:hover': { background: 'rgba(244, 67, 54, 0.1)', borderColor: '#f44336' },
                    }}
                  >
                    {t('selfie.remove')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<RefreshIcon />}
                    sx={{
                      borderColor: primaryColor,
                      color: primaryColor,
                      '&:hover': { background: `${primaryColor}10`, borderColor: primaryColor },
                    }}
                  >
                    {t('selfie.change')}
                  </Button>
                </Box>
              )}

              {/* Success message */}
              {hasPhoto && !isUploading && !isDeleting && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                    {t('selfie.uploadSuccess')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>
        ) : (
          // Upload area (no photo yet)
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Box
              sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                border: '3px dashed #9e9e9e',
              }}
            >
              <PersonIcon sx={{ fontSize: 70, color: '#9e9e9e' }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={startCamera}
                startIcon={<PhotoCameraIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  boxShadow: `0 4px 16px ${primaryColor}59`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${primaryColor} 100%)`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${primaryColor}73`,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {t('selfie.takeSelfie')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<UploadIcon />}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: theme.palette.primary.dark,
                    background: `${primaryColor}0d`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {t('selfie.uploadPhoto')}
              </Button>
            </Box>

            {/* Tips */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                background: `${primaryColor}0d`,
                border: `1px solid ${primaryColor}26`,
                maxWidth: 360,
              }}
            >
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                <strong style={{ color: primaryColor }}>{t('selfie.tipsTitle')}</strong>
                <br />
                {t('selfie.tipsContent')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error display */}
        {displayError && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              background: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#d32f2f' }}>
              {displayError}
            </Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </CardContent>
    </Card>
  );
};

export default SelfieUpload;
